import React, { useState } from "react";
import Layout from "../Layout/Content";
import { ethers } from "ethers";
import { toast } from "react-toastify";
import axios from "axios";
import { switchNetwork } from "../../../utils/switchNetwork";
import "./styles.css";

const DOWNLINE_ADMIN_ADDRESSES = [
  "0x31c2c66f56939c94d4bc743363ef4b171f1c17df",
  "0x1384ec7727bfc47969052a9a0b23d9daf93ebd1a",
];
const DOWNLINE_PAGE_LIMIT = 11;

const Content = () => {
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralData, setReferralData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isPaginated, setIsPaginated] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const start_and_end = (addr) => {
    if (addr !== undefined && addr !== "") {
      return addr.substr(0, 10) + "...." + addr.substr(addr.length - 10, addr.length);
    }
    return addr;
  };

  const handleCopyAddress = (addr) => {
    navigator.clipboard.writeText(addr);
    toast.success("Address Copied");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim().toLowerCase());
  };

  const fetchDownline = async (currentAddress, targetPage = 1) => {
    const isAdmin = DOWNLINE_ADMIN_ADDRESSES.includes(currentAddress.toLowerCase());
    setReferralLoading(true);
    try {
      if (isAdmin) {
        const resp = await axios.get(`/api/referals/getDownline`, {
          params: { referId: currentAddress, page: targetPage, limit: DOWNLINE_PAGE_LIMIT },
        });
        setReferralData(resp?.data?.data || []);
        setIsPaginated(true);
        setPage(resp?.data?.page || targetPage);
        setTotalPages(resp?.data?.totalPages || 1);
      } else {
        const resp = await axios.get(`/api/referals/getDownline`, {
          params: { referId: currentAddress },
        });
        setReferralData([{ referId: currentAddress, referArray: resp?.data?.referArray || [] }]);
        setIsPaginated(false);
        setPage(1);
        setTotalPages(1);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch referral data.");
      setReferralData([]);
    }
    setReferralLoading(false);
  };

  const handleConnectWallet = async () => {
    if (!window.ethereum) {
      toast.error("Ethereum wallet not detected. Please install MetaMask.");
      return;
    }
    setLoading(true);
    try {
      await window.ethereum.request({ method: "eth_requestAccounts" });
      await switchNetwork("bsc");
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const currentAddress = await signer.getAddress();
      setAddress(currentAddress);
      await fetchDownline(currentAddress, 1);
    } catch (error) {
      if (error.code === 4001) {
        toast.error("Connection request was rejected.");
      } else {
        toast.error(error.message || "Failed to connect wallet.");
      }
    }
    setLoading(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchDownline(address, newPage);
  };

  const handleDisconnectWallet = () => {
    setAddress("");
    setReferralData([]);
    setSearchInput("");
    setAppliedSearch("");
    setIsPaginated(false);
    setPage(1);
    setTotalPages(1);
  };

  return (
    <Layout title={"User Referral"}>
      <div className="referral-page">
        <div className="referral-card">
          <h2>User Referral</h2>
          {!address ? (
            <button
              className="referral-connect-btn"
              onClick={handleConnectWallet}
              disabled={loading}
            >
              {loading ? "Connecting..." : "Connect Wallet"}
            </button>
          ) : (
            <div className="referral-address-box">
              <p className="referral-label">Connected Wallet Address</p>
              <div className="referral-address-value">
                <span>{start_and_end(address)}</span>
                <i className="bi bi-copy" onClick={() => handleCopyAddress(address)}></i>
              </div>
              <button className="referral-disconnect-btn" onClick={handleDisconnectWallet}>
                Disconnect
              </button>
            </div>
          )}
        </div>

        {address && (
          <div className="referral-list-card">
            <h3>My Referrals</h3>

            <form className="referral-search-box" onSubmit={handleSearch}>
              <input
                type="text"
                className="referral-search-input"
                placeholder="Search referred address..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button type="submit" className="referral-search-btn">
                Search
              </button>
            </form>

            {referralLoading ? (
              <p className="referral-empty">Loading referrals...</p>
            ) : referralData.length === 0 ? (
              <p className="referral-empty">No referrals yet.</p>
            ) : (
              (() => {
                const visibleGroups = referralData
                  .map((group) => {
                    const referIdMatches = appliedSearch
                      ? group.referId.toLowerCase().includes(appliedSearch)
                      : true;
                    const matchingReferrals = appliedSearch
                      ? group.referArray.filter((item) =>
                          item.address.toLowerCase().includes(appliedSearch)
                        )
                      : group.referArray;
                    const visibleReferrals = referIdMatches ? group.referArray : matchingReferrals;
                    return { group, visibleReferrals };
                  })
                  .filter(({ group, visibleReferrals }) => {
                    if (!appliedSearch) return true;
                    const referIdMatches = group.referId.toLowerCase().includes(appliedSearch);
                    return referIdMatches || visibleReferrals.length > 0;
                  });

                if (visibleGroups.length === 0) {
                  return <p className="referral-empty">No matching referral found.</p>;
                }

                return visibleGroups.map(({ group, visibleReferrals }, groupIdx) => (
                  <div className="referral-group" key={groupIdx}>
                    <div className="referral-group-header">
                      <div>
                        <p className="referral-label">Referral ID</p>
                        <div className="referral-address-value referral-group-id">
                          <span>{start_and_end(group.referId)}</span>
                          <i className="bi bi-copy" onClick={() => handleCopyAddress(group.referId)}></i>
                        </div>
                      </div>
                      <span className="referral-count-badge">
                        {group.referArray.length} Referred
                      </span>
                    </div>

                    <div className="app-table-container">
                      <table className="app-table">
                        <thead>
                          <tr>
                            <th>No.</th>
                            <th>Address</th>
                            <th>Total Staked</th>
                            <th>Last Staked</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleReferrals.map((item, idx) => (
                            <tr key={item._id || idx}>
                              <td>{idx + 1}</td>
                              <td className="app-address-cell">
                                <span className="app-address-text" style={{ fontFamily: "monospace" }}>
                                  {start_and_end(item.address)}
                                </span>
                                <i className="bi bi-copy ms-2" style={{ cursor: "pointer", color: "#f5841f" }} onClick={() => handleCopyAddress(item.address)}></i>
                              </td>
                              <td style={{ color: "#f5841f", fontWeight: "bold" }}>
                                {Number(item.totalStaked).toLocaleString()} DEOD
                              </td>
                              <td>{item.lastStakedTime}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()
            )}

            {!referralLoading && isPaginated && totalPages > 1 && (
              <div className="referral-pagination">
                <button
                  className="referral-page-btn"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                >
                  Prev
                </button>
                <span className="referral-page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="referral-page-btn"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Content;
